/* eslint new-cap: 0 */

import {
  createBrowserRouter,
  isRouteErrorResponse,
  Navigate,
  RouteObject,
  RouterProvider,
  useLocation,
} from "react-router-dom";

import * as Sentry from '@sentry/react';

/* containers */
import LoginView from "./components/LoginView";
import ChangePasswordView from "./components/ChangePassword/ChangePasswordView";
import {LoadingAnimation} from "./components/LoadingAnimation";
import ReclamATC from "./components/ReclamATC/components/ReclamATC";
import CRMForm from "./components/CRMForm/components/CRMForm";
import UnsignedCRM from "./components/UnsignedCRM";
import ErrorRoutePage from "./components/Error/ErrorRoutePage";
import DinamicServerPage from "./components/ServerElements/components/DinamicServerPage";

import SignUpView from "./components/SignUp";
import ContractsTabs from "./components/Contracts/ContractsTabs";
import ContractDetailElectricity from "./components/Contracts/ContractDetailElectricity";
import ContractDetailGas from "./components/Contracts/ContractDetailGas";
import InvoicesTabs from "./components/Invoices/components/InvoicesTabs";
import InvoicesMultipoint from "./components/Invoices/components/InvoicesMultipointContent";
import Contractation from "./components/Contractation/Contractation";
import ElectricityContractation from "./components/Contractation/ElectricityContractation";
import GasContractation from "./components/Contractation/GasContractation";
import UserResetPassword from "./components/UserResetPassword";
import ComparatorPower from "./components/ComparatorPower";
import ComparatorPrice from "./components/ComparatorPrice";
import GasPriceComparator from "./components/GasPriceComparator";

import Home from "./components/Home";

import RootPage from "./components/RootPage";
import useI18n from "./hooks/useI18n";
import { AuthProtectionProvider } from "./components/Authentication/AuthProtectionProvider";
import { NavLanguageProvider } from "./components/NavLangProvider";
import { LayoutProvider } from "./components/LayoutProvider";
import NewsletterPage from "./components/Newsletter/NewsletterPage";
import FeatureFlag from "./components/FeatureFlag/FeatureFlag";
import Omip from "./components/Omip";
import { useSettingsContext } from "./components/Settings/hooks/useSettingsContext";
import ErrorComponentPage from "./components/Error/ErrorComponentPage";
import ErrorBoundaryComponent from "./components/Error/ErrorBoundaryComponent";
import ErrorComponentSearchParams from "./components/Error/ErrorComponentSearchParams";
import { AuthChecker } from "./components/Authentication/AuthChecker";
import { Settings } from "./components/Settings/types/settings";

type CustomRouteProps = {
  path: string;
  protection: "private" | "public" | "unprotected";
  isError?: boolean;
} &  RouteObject;

export const generateTranslatedRouteElements = ({ 
  path, protection, element, isError = false, ...rest 
}: CustomRouteProps) => {
  const { getAllLangsPath } = useI18n();

  return getAllLangsPath(path).map(translatedPath => ({
    path: translatedPath.replace(/{{/g, ":").replace(/}}/g, ""),
    element: (
      <NavLanguageProvider path={path}>
        <LayoutProvider path={path} isError={isError}>
          <AuthProtectionProvider path={path} protection={protection}>
            {element}
          </AuthProtectionProvider>
        </LayoutProvider>
      </NavLanguageProvider>
    ),
    ...rest
  } as RouteObject));
};


export const createRoutes = (Settings: Settings): RouteObject[] => {
  return [
    {
      path: "/",
      element: <AuthChecker><RootPage /></AuthChecker>,
      errorElement: <LayoutProvider isError={true}>
        <ErrorComponentPage />
      </LayoutProvider>,
      children: [
        
        {
          errorElement: <LayoutProvider isError={true}>
            <ErrorBoundaryComponent />
          </LayoutProvider>,
          children: [
  
            {
              index: true,
              element: (
                <NavLanguageProvider>
                  <LayoutProvider>
                    <AuthProtectionProvider protection={Settings.home?.type == "tabGroups" ? "unprotected" : "private"}>
                      <Home/>
                    </AuthProtectionProvider>
                  </LayoutProvider>
                </NavLanguageProvider>
              )
            },
    
            {
              path: "*",
              loader: () => {throw new Response("Not Found", { status: 404 })}
            },
    
            // PRIVATE ----------------------------------------------------------- /
    
            ...generateTranslatedRouteElements({
              path: "common:url.invoices",
              element: <InvoicesTabs/>,
              protection: "private",
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.invoices_multipoint",
              element: <InvoicesMultipoint />,
              protection: "private",
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.contracts.electricity.default",
              element: <ContractsTabs initContractType="electricity"/>,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.contracts.gas.default",
              element: <ContractsTabs initContractType="gas"/>,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.contracts.electricity.detail",
              element: <ContractDetailElectricity />,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.contracts.gas.detail",
              element: <ContractDetailGas />,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.contact",
              element: <FeatureFlag flagName="crm.case"><CRMForm/></FeatureFlag>,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.reclamations",
              element: <ReclamATC/>,
              protection: "private"
            }),
            ...generateTranslatedRouteElements({
              path: "common:url.changePassword",
              element: <ChangePasswordView/>,
              protection: "private"
            }),
            ...(Settings?.features?.oidc ? generateTranslatedRouteElements({
              path: "common:url.connect",
              element: <LoadingAnimation/>,
              protection: "private"
            }) : []),
            ...(Settings?.features?.enableServerElements ? generateTranslatedRouteElements({
              path: "common:url.dynamic",
              element: <DinamicServerPage/>,
              protection: "private"
            }) : []),
            ...generateTranslatedRouteElements({
              path: "common:url.omip",
              element: <Omip/>,
              protection: "private"
            }),
    
            // Contractation
            ...(Settings?.contractation?.authenticate ? [
              ...(Settings.contractation?.electricity?.enabled ? 
                generateTranslatedRouteElements({
                  path: "common:url.electricityContractation",
                  element: <Contractation><ElectricityContractation/></Contractation>,
                  protection: "private"
                }) : []),
              ...(Settings.contractation?.dual?.enabled ? 
                  generateTranslatedRouteElements({
                  path: "common:url.dualContractation",
                  element: <Contractation><ElectricityContractation dualContractation={true}/></Contractation>,
                  protection: "private"
                }) : []),
              ...(Settings.contractation?.gas?.enabled ?
                generateTranslatedRouteElements({
                  path: "common:url.gasContractation",
                  element: <Contractation><GasContractation /></Contractation>,
                  protection: "private"
                }) : [])
            ] : []),
    
    
            // PUBLIC ------------------------------------------------------------ /
    
            ...(!Settings?.features?.oidc
            ? generateTranslatedRouteElements({
              path: "common:url.login",
              element: <LoginView />,
              protection: "public"
            }) : []),
            ...generateTranslatedRouteElements({
              path: "common:url.unsigned_contact",
              element: <UnsignedCRM/>,
              protection: "public"
            }),
            ...(Settings?.features?.signup ? generateTranslatedRouteElements({
              path: "common:url.activate",
              element: <SignUpView/>,
              protection: "public"
            }): []),
            ...generateTranslatedRouteElements({
              path: "common:url.resetPassword",
              element: <UserResetPassword/>,
              protection: "public"
            }),
            
            
            // UNPROTECTED ------------------------------------------------------ /
            
            ...generateTranslatedRouteElements({
              path: "common:url.error",
              element: <ErrorComponentSearchParams />,
              isError: true,
              protection: "unprotected"
            }),
  
            ...(Settings?.features?.oidc && Settings?.oidc?.routes?.allowDefaultLogin
            ? generateTranslatedRouteElements({
              path: "common:url.login",
              element: <LoginView />,
              protection: "unprotected"
            }) : []),
            ...(Settings?.powerCalculator?.enabled ? generateTranslatedRouteElements({
              path: "common:url.powerCalculator",
              element: <ComparatorPower/>,
              protection: "unprotected"
            }) : []),
            ...(Settings?.comparator?.enabled ? generateTranslatedRouteElements({
              path: "common:url.priceComparator",
              element: <ComparatorPrice/>,
              protection: "unprotected"
            }) : []),
            ...(Settings?.comparator?.enabled && 'gas' in Settings?.features?.displayTypes ? generateTranslatedRouteElements({
              path: "common:url.gasPriceComparator",
              element: <GasPriceComparator/>,
              protection: "unprotected"
            }) : []),
            ...generateTranslatedRouteElements({
              path: "common:url.loading",
              element: <LoadingAnimation/>,
              protection: "unprotected"
            }),
            ...(Settings?.newsletter?.enabled ? generateTranslatedRouteElements({
              path: "common:url.newsletter",
              element: <NewsletterPage/>,
              protection: "unprotected"
            }) : []),
    
            // Contractation
            ...(!Settings?.contractation?.authenticate ? [
              ...(Settings.contractation?.electricity?.enabled ? 
                generateTranslatedRouteElements({
                  path: "common:url.electricityContractation",
                  element: <Contractation><ElectricityContractation/></Contractation>,
                  protection: "unprotected"
                }) : []),
              ...(Settings.contractation?.dual?.enabled ? 
                  generateTranslatedRouteElements({
                  path: "common:url.dualContractation",
                  element: <Contractation><ElectricityContractation dualContractation={true}/></Contractation>,
                  protection: "unprotected"
                }) : []),
              ...(Settings.contractation?.gas?.enabled ?
                generateTranslatedRouteElements({
                  path: "common:url.gasContractation",
                  element: <Contractation><GasContractation /></Contractation>,
                  protection: "unprotected"
                }) : [])
            ] : [])
          ]
        }
          
      ]
    }
  ];
}

const RouterConfiguration = () => {
  const Settings = useSettingsContext();

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
    createBrowserRouter
  );

  const routes = createRoutes(Settings);

  const sentryWrappedRouter = sentryCreateBrowserRouter(routes);

  const maintenanceRouter = createBrowserRouter([
    {
      path: "*",
      element: <Navigate to={"/"} />,
    },
    {
      path: "/",
      element: <LayoutProvider isError={true}><ErrorComponentPage status={503}/></LayoutProvider>,
    }
  ]);

  return <RouterProvider
      router={ Settings.maintenance?.activated ? 
        maintenanceRouter : sentryWrappedRouter} 
    />
};

export default RouterConfiguration;
